import { CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/form/baseInputs/Button';
import { Upload01Icon } from '@/components/icons/Upload01Icon';
import { BigBreadcrumbs } from '@/components/navigation/BigBreadcrumbs';
import { LinkTabProps } from '@/components/navigation/NavigationTabs';
import { PageHeader } from '@/components/navigation/PageHeader';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useMatchingRoutes } from '@/hooks/useMatchingRoutes';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import {
  ClientDocumentsPageContext,
  useClientDocumentsPageContext,
} from '@/modules/documents/contexts/clientDocumentsPage.context';
import { useDownloadDocument } from '@/modules/documents/hooks/useDownloadPresentationDocument';
import { MultiDocumentUploaderProvider } from '@/modules/documents/MultiDocumentUploader/context/MultiDocumentUploader.provider';
import { HargroveConsultationButton } from '@/modules/estateInsights/MissingDocumentsPanel/HargroveConsultationButton';
import { MissingDocumentsButton } from '@/modules/estateInsights/MissingDocumentsPanel/MissingDocumentsButton';
import { MissingDocumentsPanel } from '@/modules/estateInsights/MissingDocumentsPanel/MissingDocumentsPanel';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useMemoPaths } from '@/modules/memos/hooks/useMemoPaths';
import { MissingDocumentsContextProvider } from '@/modules/missingDocuments/contexts/MissingDocumentsContext';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { useCurrentUserWorkflowTrackersContext } from '@/modules/workflowTrackers/contexts/useCurrentUserWorkflowTrackersContext';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import {
  DocumentType,
  WorkflowTrackerKind,
  WorkflowTrackerStatus,
} from '@/types/schema';

import { DocumentBreadcrumb } from '../DocumentDetailsPage/DocumentBreadcrumb';

// Re-export for backward compatibility with existing consumers.
export { ClientDocumentsPageContext, useClientDocumentsPageContext };

export function ClientDocumentsPage() {
  const navigate = useNavigate();
  const householdId = useRequiredParam('householdId');
  const params = useParams();
  const documentId = params.documentId ?? null;
  const memoId = params.memoId ?? null;
  const aiCapabilitiesEnabled = useAICapabilitiesEnabled();
  const memosEnabled = aiCapabilitiesEnabled;
  // When memos_in_studio is enabled, memos live under the Studio. The
  // Documents page hides its Memos tab and redirects its memo routes, so
  // existing links and notifications open the Studio.
  const isMemosInStudioEnabled = useFeatureFlag('memos_in_studio');
  const { memoListPath, getMemoDetailsPath } = useMemoPaths(householdId);
  const { isMatchingRoute: isMemosRoute } = useMatchingRoutes([
    ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS_MEMOS,
    ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS,
  ]);
  const isMemosView = memosEnabled && isMemosRoute;
  const isDocumentsView = !isMemosView;
  const showMissingDocuments = isDocumentsView && !documentId && !memoId;
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [
    shouldOpenDocumentModalOnDocumentsRoute,
    setShouldOpenDocumentModalOnDocumentsRoute,
  ] = useState(false);
  const { loading: isDownloading } = useDownloadDocument();

  const { trackers } = useCurrentUserWorkflowTrackersContext();
  const inProgressSplitBanner = useMemo(() => {
    const inProgressSplit = trackers.find(
      (tracker) =>
        tracker.kind === WorkflowTrackerKind.DocumentSplitBinder &&
        tracker.status === WorkflowTrackerStatus.Started &&
        tracker.householdID === householdId
    );

    if (!inProgressSplit) return null;

    const isHouseholdExport = inProgressSplit.documents?.some(
      (doc) => doc.type === DocumentType.HouseholdExport
    );

    if (isHouseholdExport) {
      return {
        title: 'Processing household export',
        body: 'Suggestions for people and estate data will appear when ready.',
      };
    }

    return {
      title: 'Processing document binder',
      body: 'Your document binder is processing and additional documents will appear in the table below when ready.',
    };
  }, [trackers, householdId]);

  useEffect(() => {
    if (!isMemosRoute) return;
    if (!memosEnabled) {
      navigate(
        getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS, {
          householdId,
        }),
        { replace: true }
      );
      return;
    }
    if (isMemosInStudioEnabled) {
      navigate(memoId ? getMemoDetailsPath(memoId) : memoListPath, {
        replace: true,
      });
    }
  }, [
    getMemoDetailsPath,
    householdId,
    isMemosRoute,
    isMemosInStudioEnabled,
    memoId,
    memoListPath,
    memosEnabled,
    navigate,
  ]);

  useEffect(() => {
    if (!isDocumentsView && documentModalOpen) {
      setDocumentModalOpen(false);
    }
  }, [documentModalOpen, isDocumentsView]);

  useEffect(() => {
    if (!isDocumentsView || !shouldOpenDocumentModalOnDocumentsRoute) {
      return;
    }

    setDocumentModalOpen(true);
    setShouldOpenDocumentModalOnDocumentsRoute(false);
  }, [isDocumentsView, shouldOpenDocumentModalOnDocumentsRoute]);

  const tabs = useMemo((): LinkTabProps[] | undefined => {
    const tabList: LinkTabProps[] = [
      {
        display: 'Documents',
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
          {
            householdId,
          }
        ),
        isActive: isDocumentsView,
        dataTestId: 'documents-tab-documents',
      },
    ];

    if (memosEnabled && !isMemosInStudioEnabled) {
      tabList.push({
        display: 'Memos',
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS_MEMOS,
          { householdId }
        ),
        isActive: isMemosView,
        dataTestId: 'documents-tab-memos',
      });
    }

    return tabList;
  }, [
    householdId,
    isDocumentsView,
    isMemosView,
    isMemosInStudioEnabled,
    memosEnabled,
  ]);

  const heading = useMemo(() => {
    if (!documentId && !memoId)
      return <BigBreadcrumbs crumbs={[{ label: 'Documents' }]} />;

    return (
      <DocumentBreadcrumb
        documentCrumbLabel={memoId ? 'Memo details' : 'Document details'}
        rootPath={getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
          {
            householdId,
          }
        )}
      />
    );
  }, [documentId, householdId, memoId]);

  const action = useMemo(() => {
    if (documentId || memoId) {
      return;
    }

    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
      >
        {showMissingDocuments && (
          <>
            {isDownloading && <CircularProgress size={24} />}
            <HargroveConsultationButton />
            <MissingDocumentsButton />
          </>
        )}
        <Button
          size="sm"
          onClick={() => {
            if (isMemosView) {
              setShouldOpenDocumentModalOnDocumentsRoute(true);
              navigate(
                getCompletePathFromRouteKey(
                  ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
                  {
                    householdId,
                  }
                ),
                { replace: true }
              );
              return;
            }

            setDocumentModalOpen(true);
          }}
          variant="primary"
          startIcon={Upload01Icon}
        >
          Upload documents
        </Button>
      </Stack>
    );
  }, [
    documentId,
    householdId,
    isDownloading,
    isMemosView,
    memoId,
    navigate,
    showMissingDocuments,
  ]);

  if (isMemosRoute && (!memosEnabled || isMemosInStudioEnabled)) {
    return null;
  }

  return (
    <MissingDocumentsContextProvider pollForUpdates={showMissingDocuments}>
      {showMissingDocuments && <MissingDocumentsPanel />}
      <MultiDocumentUploaderProvider>
        <ClientDocumentsPageContext.Provider
          value={{
            modalOpen: documentModalOpen,
            setModalOpen: setDocumentModalOpen,
          }}
        >
          <Stack height="100%">
            <PageHeader heading={heading} action={action} tabs={tabs} />
            {!documentId &&
              !memoId &&
              isDocumentsView &&
              inProgressSplitBanner && (
                <Stack px={2} pt={2}>
                  <Callout severity="success">
                    <Typography variant="title-xs" paddingBottom={0.5}>
                      {inProgressSplitBanner.title}
                    </Typography>

                    <Typography variant="body-xs">
                      {inProgressSplitBanner.body}
                    </Typography>
                  </Callout>
                </Stack>
              )}
            <Stack
              p={2}
              flex={1}
              minHeight={0}
              spacing={2}
              direction="row"
              width="100%"
            >
              <Stack display="flex" flex={1} minWidth={0} minHeight={0}>
                <Outlet />
              </Stack>
            </Stack>
          </Stack>
        </ClientDocumentsPageContext.Provider>
      </MultiDocumentUploaderProvider>
    </MissingDocumentsContextProvider>
  );
}
